import type { Ref } from "vue";
import type { Router } from "vue-router";
import type { Store } from "vuex";
import {
  DeliverableAreaDeliveryIdInput,
  ListDeliverableAreaDeliveryIdQuery,
  ListDeliverableAreaDeliveryIdDocument,
} from "@/generated/graphql";
import { localStorageGetCustomerCode } from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { routerPath } from "@/common/router";
import scheduledDeliveryStore from "@/store/scheduledDeliveryStore";
import { ApolloQueryResult } from "@apollo/client/core";

/**
 * 登録フォームの入力状態と操作を提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useScheduledDeliveryRegistForm = (params: {
  chainSelectValues: Ref<{ chain_code: string; name: string } | null>;
  shopSelectFromValues: Ref<{ shop_code: string; name: string }>;
  shopSelectToValues: Ref<{ shop_code: string; name: string }>;
  deliverableAreaDeliveryIdInput: DeliverableAreaDeliveryIdInput;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  router: Router;
  store: Store<unknown>;
}) => {
  /**
   * 配達便存在チェック
   */
  const chkDeliveryId = async () => {
    try {
      //graphql接続
      const apolloClient = await generateApolloClient();

      params.deliverableAreaDeliveryIdInput.customer_code =
        localStorageGetCustomerCode();
      params.deliverableAreaDeliveryIdInput.chain_code =
        params.chainSelectValues.value!.chain_code;
      params.deliverableAreaDeliveryIdInput.shop_code =
        params.shopSelectFromValues.value.shop_code;

      // 配達便が存在しているかチェック
      const deliverableAreaDeliveryIdResp: ApolloQueryResult<ListDeliverableAreaDeliveryIdQuery> =
        await apolloClient.query<ListDeliverableAreaDeliveryIdQuery>({
          query: ListDeliverableAreaDeliveryIdDocument,
          variables: {
            input: params.deliverableAreaDeliveryIdInput,
          },
        });
      //存在していない場合エラー
      if (
        deliverableAreaDeliveryIdResp.data.listDeliverableAreaDeliveryId
          ?.length == 0
      ) {
        params.errdialog.value = true;
        params.errMessage.value =
          "選択された配達先は、出荷元の配達エリアに\r\n存在しない店舗です。";
        return false;
      } else {
        return true;
      }
    } catch (err: unknown) {
      console.error(err);
      params.errdialog.value = true;
      if (err instanceof Error) {
        params.errMessage.value = err.message;
      } else {
        params.errMessage.value = "不明なエラーが発生しました。";
      }
      return false;
    }
  };

  /**
   * 「登録手続きへ」ボタン @Click
   */
  const onClickOpenInputDialog = async (): Promise<void> => {
    // 配達先が配達エリアに存在するかチェック
    const result = await chkDeliveryId();
    if (result == false) {
      return;
    }

    // 「店舗間定期配達登録-入力」画面へ遷移
    await scheduledDeliveryStore.setupValue({
      chain_code: params.chainSelectValues.value!.chain_code,
      shop_code_from: params.shopSelectFromValues.value.shop_code,
      shop_code_to: params.shopSelectToValues.value.shop_code,
      scheduled_delivery_no: "",
      processFlag: 0,
    });
    await params.router.push({ path: routerPath.scheduledDeliveryRegister });
    params.store.commit("listEditOn");
  };

  return {
    chkDeliveryId,
    onClickOpenInputDialog,
  };
};
